import type { PaymentMethod } from 'gw-edge-client/dist/types/dto/bc/typekey';
import type { ProductName_Top } from 'gw-edge-client/dist/types/dto/pc/typekey';

enum LOB_CODE {
    PRIVATE_HOUSE = 'PRH',
    PET = 'PET',
    PRIVATE_CONTENT = 'PRC',
    CHO = 'CHO',
    ACCIDENT = 'ACC',
    TRAVEL = 'TRA',
    UNPOWERED_VEHICLE_PRIVATE = 'UVP',
    MVP = 'MVP',
    PROPERTY = 'PRA'
}

enum FLAVOUR_NAME {
    DOG = 'Hundeforsikring',
    CAT = 'Katteforsikring',
    KENNEL = 'Kennelforsikring',
    STC_SINGLE_TRIP_CANCELLATION = 'Afbestillingsforsikring til enkeltrejse',
    STT_SINGLE_TRIP_TRAVEL = 'Enkeltrejseforsikring',
    ACC_ACCIDENT = 'Ulykkesforsikring',
    ACC_YOUNG = 'Ulykkesforsikring til unge',
    ACC_CHILD = 'Børneforsikring',
    ACC_CROSS = 'Krydsulykkesforsikring',
    TRA_HOUSEHOLD = 'Rejseforsikring',
    TRA_INDIVIDUAL = 'Rejseforsikring til enkeltperson',
    PRC_YOUTH_CONTENT = 'Indboforsikring til unge',
    PRC_CONTENT = 'Indboforsikring',
    MVP_CAR = 'Bilforsikring',
    CHO = 'Ejerskifteforsikring',
    PRH_HOUSE = 'Husforsikring',
    PRH_HOLIDAY_HOME = 'Fritidshusforsikring',
    UVP_CARAVAN = 'Campingvognsforsikring',
    UVP_TRAILER = 'Påhængsvognsforsikring',
    CTP_AGRICULTURE_CONTENT = 'Privat løsøre- og ansvarsforsikring',
    CTV_AGRICULTURE_CONTENT = 'Landbrugsløsøre- og driftstabsforsikring',
    LBV_LIABILITY = 'Landbrugsansvarsforsikring',
    CPR_PROPERTY = 'Ejendomsforsikring',
    CVR_PROPERTY = 'Ejendomsforsikring',
    CPR_MACHINERY = 'Maskinforsikring',
    CVR_MACHINERY = 'Maskinforsikring',
    CPR_WORKER_COMP = 'Arbejdsskadeforsikring',
    CVR_WORKER_COMP = 'Arbejdsskadeforsikring'
}

const COVERABLE_CODE: Record<string, string> = {
    dog: 'topdanmark_dog',
    cat: 'topdanmark_cat',
    kennel: 'topdanmark_kennel',
    accident: 'top_acc_accident',
    childAccident: 'top_acc_child',
    youngAccident: 'top_acc_young',
    crossAccident: 'top_acc_cross',
    privateContent: 'top_prc_content',
    youthContent: 'top_prc_youth_content',
    travel: 'top_tra_household',
    travelHousehold: 'top_tra_household',
    travelIndividual: 'top_tra_individual',
    stc: 'STCSingleTripCancellationInsurance',
    singleTripTravelCancellation: 'STCSingleTripCancellationInsurance',
    stt: 'STTSingleTripTravelInsurance',
    singleTripTravel: 'STTSingleTripTravelInsurance',
    car: 'top_mvp_car',
    cho: 'top_cho_insurance',
    prhHouse: 'top_prh_house',
    house: 'top_prh_house',
    prhHolidayHome: 'top_prh_holiday_home',
    holidayHome: 'top_prh_holiday_home',
    changeOfOwnership: 'top_cho_insurance',
    caravan: 'top_uvp_caravan',
    trailer: 'top_uvp_trailer'
};

const FLAVOUR_NAME_MAP: Record<ProductName_Top, string> = {
    top_acc_accident: 'ulykkesforsikring',
    top_acc_child: 'børneforsikring',
    top_tra_individual: 'rejseforsikring til enkeltperson',
    top_tra_household: 'rejseforsikring',
    top_prc_content: 'indboforsikring',
    topdanmark_cat: 'katteforsikring',
    topdanmark_dog: 'hundeforsikring',
    topdanmark_kennel: 'kennelforsikring',
    top_acc_young: 'ulykkesforsikring til unge',
    top_prc_youth_content: 'indboforsikring til unge',
    STCSingleTripCancellationInsurance: 'afbestillingsforsikring til enkeltrejse',
    STTSingleTripTravelInsurance: 'enkeltrejseforsikring',
    top_mvp_car: 'bilforsikring',
    top_cho_insurance: 'ejerskifteforsikring',
    top_prh_house: 'husforsikring',
    top_prh_holiday_home: 'fritidshusforsikring',
    top_uvp_caravan: 'Campingvognsforsikring',
    top_uvp_trailer: 'Påhængsvognsforsikring'
};

const DETAILS_PAGE_FLAVOUR_URL: Record<ProductName_Top, string> = {
    top_acc_accident: 'ulykkesforsikring',
    top_acc_child: 'boerneforsikring',
    top_tra_individual: 'rejseforsikring',
    top_tra_household: 'rejseforsikring',
    top_prc_content: 'indboforsikring',
    topdanmark_cat: 'katteforsikring',
    topdanmark_dog: 'hundeforsikring',
    topdanmark_kennel: 'kennelforsikring',
    top_acc_young: 'ulykkesforsikring-til-unge',
    top_prc_youth_content: 'indboforsikring-til-unge',
    STCSingleTripCancellationInsurance: 'afbestillingsforsikring-til-enkeltrejse',
    STTSingleTripTravelInsurance: 'enkeltrejseforsikring',
    top_mvp_car: 'bilforsikring',
    top_cho_insurance: 'ejerskifteforsikring',
    top_prh_house: 'husforsikring',
    top_prh_holiday_home: 'fritidshusforsikring',
    top_uvp_caravan: 'Campingvognsforsikring',
    top_uvp_trailer: 'Påhængsvognsforsikring'
};

const PAYMENT_METHOD_MAP: Record<PaymentMethod, string> = {
    BS_Top: 'BS',
    FIK_Top: 'FIK',
    MPS_Top: 'MPS',
    ach: '',
    BankTransfer_Top: '',
    misc: '',
    EAN_Top: '',
    accountunapplied: '',
    internaltransfer_Top: '',
    cash: '',
    check: '',
    creditcard: '',
    ManualPayment: '',
    ManualSuspense: '',
    MPI_Top: '',
    responsive: '',
    producerunapplied: '',
    wire: ''
};

const getPaymentMethod = (paymentMethod: string | undefined): string => {
    return PAYMENT_METHOD_MAP[paymentMethod as PaymentMethod];
};

export {
    COVERABLE_CODE,
    FLAVOUR_NAME_MAP,
    DETAILS_PAGE_FLAVOUR_URL,
    LOB_CODE,
    FLAVOUR_NAME,
    getPaymentMethod
};
